import {
  combinedClassNameSelector,
  makeModuleItemsSelector,
  makeModuleStateSelector,
  makeTextHeadingLevelSelector
} from "@client/content/selectors";
import { getEntitySelector } from "@client/lib/layout";
import { expandTagsArray, getCTItemTags } from "@client/lib/tags";
import {
  TargetSelector,
  combinedSettingsSelector,
  combinedStyleSelector,
  createShallowEqualSelector,
  entitiesSourcePropsSelector,
  getCompositeSettingSelector,
  getSettingValueSelector,
  idPropsSelector,
  siteColorSelector
} from "@client/selectors";
import { type LabelValue } from "@client/types";
import classNames from "classnames";
import { createSelector } from "reselect";
import { TextComponentPropsSelector } from "../selectors";
import { type PortfolioGalleryItemContainerProps } from "./Item/container";
import { SHOW_ALL_TAG } from "./lib";
import { defaultState, previewState } from "./reducer";
import { type PortfolioGalleryAppState } from "./typings";

const localStateSelector = makeModuleStateSelector<PortfolioGalleryAppState>(defaultState, previewState);

const makeModuleTagsSelector = () => {
  const moduleItems = makeModuleItemsSelector();
  return createSelector([moduleItems], (items) => items.map((item) => item.settings!.tags));
};

const makeTagsSelector = () => {
  const moduleTagsSelector = makeModuleTagsSelector();
  return createSelector([moduleTagsSelector], expandTagsArray);
};

export const makeAllTagsSelector = () => {
  const tagsSelector = makeTagsSelector();
  return createSelector([tagsSelector], (tags): LabelValue<string>[] =>
    [SHOW_ALL_TAG, ...tags].map((t) => ({ label: t.text, value: t.id }))
  );
};

export const makeActiveTagIdSelector = () =>
  createSelector([localStateSelector], (state: PortfolioGalleryAppState) => state.activeTagId);

export const makeActiveTagItemsIdsSelector = () => {
  const activeTagIdSelector = createSelector(
    [localStateSelector],
    (state: PortfolioGalleryAppState) => state.activeTagId
  );
  const itemsSelector = makeModuleItemsSelector();
  return createSelector([itemsSelector, activeTagIdSelector], (items, activeTagId): string[] =>
    activeTagId === SHOW_ALL_TAG.id
      ? items.map((item) => item.id)
      : items.filter((item) => getCTItemTags(item).some((item) => item.id === activeTagId)).map((item) => item.id)
  );
};

export const makeClassNameSelector = () => {
  const classNameSelector = combinedClassNameSelector(
    TargetSelector.Entity,
    ["position", "hoverBehaviour"],
    "wsc_content_portfolio_gallery",
    "content"
  );
  const settingsSelector = combinedSettingsSelector(
    TargetSelector.Entity,
    ["verticalAlign", "horizontalAlign"],
    "content"
  );

  return createSelector([classNameSelector, settingsSelector], (className, settings): string =>
    classNames(className, `hor_${settings.horizontalAlign}`, `vert_${settings.verticalAlign}`)
  );
};

export const makeItemPropsSelector = () => {
  const imageStyleSelector = combinedStyleSelector(TargetSelector.Entity, ["border", "boxShadow"], "image");
  const overlayStyleSelector = makeOverlayStyleSelector();
  const levelSelector = makeTextHeadingLevelSelector(TargetSelector.Entity, "content.title");

  return createSelector(
    [
      idPropsSelector,
      entitiesSourcePropsSelector,
      imageStyleSelector,
      TextComponentPropsSelector,
      levelSelector,
      overlayStyleSelector
    ],
    (
      id,
      source,
      imageStyle,
      TextComponent,
      level,
      overlayStyle
    ): Omit<PortfolioGalleryItemContainerProps, "context" | "id" | "StaticContainer"> => ({
      imageStyle,
      parentId: id,
      source,
      TextComponent,
      overlayStyle,
      titleTag: level
    })
  );
};

export const makeMasonryCssDataSelector = () => {
  const settingsSelector = combinedSettingsSelector(TargetSelector.Entity, ["prtfColumns", "prtfSpace"]);

  return createShallowEqualSelector([idPropsSelector, settingsSelector], (id, settings): object => {
    const columnCount = settings.prtfColumns.match(/\d+/)[0] || 4;
    const gutter = +settings.prtfSpace;
    const columnSize = `calc((100% - ${gutter * (columnCount - 1)}px) / ${columnCount})`;

    return {
      selector: getEntitySelector(id),
      columnSize,
      gutterSize: `${gutter}px`
    };
  });
};

export const makeOverlayStyleSelector = () => {
  const overlaySettingSelector = getCompositeSettingSelector<{ color: string }>(
    TargetSelector.Entity,
    "overlay",
    "content"
  );
  const contentPositionSelector = getSettingValueSelector<"inside" | "outside">(
    TargetSelector.Entity,
    "position",
    "content"
  );

  return createShallowEqualSelector(
    [overlaySettingSelector, contentPositionSelector, siteColorSelector],
    (overlay, contentPosition, siteColor): React.CSSProperties | undefined =>
      !!overlay && contentPosition === "inside"
        ? {
            backgroundColor: overlay.color ?? siteColor
          }
        : undefined
  );
};
